import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import {
  Tabs, TabsContent, TabsList, TabsTrigger
} from '@/components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import {
  Plus, Search, Eye, FileText, AlertTriangle, CheckCircle2,
  Clock, XCircle, Send, IndianRupee, Calendar, Building2,
  RefreshCw, Filter, ChevronRight, CalendarIcon
} from 'lucide-react';
import { formatCurrency } from '@/utils/formatters';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric'
  });
};

const getStatusBadge = (status) => {
  const styles = {
    submitted: { variant: 'outline', className: 'border-blue-500 text-blue-600', label: 'Submitted' },
    clarification_requested: { variant: 'outline', className: 'border-amber-500 text-amber-600', label: 'Clarification Needed' },
    resubmitted: { variant: 'outline', className: 'border-blue-500 text-blue-600', label: 'Resubmitted' },
    approved: { variant: 'outline', className: 'border-green-500 text-green-600', label: 'Approved' },
    rejected: { variant: 'outline', className: 'border-red-500 text-red-600', label: 'Rejected' },
    due: { variant: 'default', className: 'bg-amber-500', label: 'Due' },
    overdue: { variant: 'default', className: 'bg-red-500', label: 'Overdue' },
    modification_requested: { variant: 'outline', className: 'border-purple-500 text-purple-600', label: 'Modification Requested' },
    re_approved: { variant: 'outline', className: 'border-green-500 text-green-600', label: 'Re-Approved' },
    paid: { variant: 'default', className: 'bg-green-600', label: 'Paid' },
    cancelled: { variant: 'outline', className: 'border-gray-500 text-gray-600', label: 'Cancelled' },
  };
  const style = styles[status] || { variant: 'outline', className: '', label: status };
  return <Badge variant={style.variant} className={style.className}>{style.label}</Badge>;
};

const getPriorityBadge = (priority) => {
  const styles = {
    normal: { className: 'bg-gray-100 text-gray-700', label: 'Normal' },
    urgent: { className: 'bg-amber-100 text-amber-700', label: 'Urgent' },
    critical: { className: 'bg-red-100 text-red-700', label: 'Critical' },
  };
  const style = styles[priority] || styles.normal;
  return <Badge variant="outline" className={style.className}>{style.label}</Badge>;
};

export default function PaymentRequestsPage() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [suppliers, setSuppliers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  
  // Filters
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSupplier, setFilterSupplier] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Create dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    supplier_id: '',
    supplier_name: '',
    payment_type: 'against_invoice',
    amount_requested: '',
    payment_due_date: '',
    purpose: '',
    priority: 'normal',
    linked_grns: [],
    linked_pos: [],
    remarks: ''
  });
  const [supplierOutstanding, setSupplierOutstanding] = useState(null);
  const [creating, setCreating] = useState(false);
  
  // View dialog
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  
  // Resubmit dialog
  const [resubmitDialogOpen, setResubmitDialogOpen] = useState(false);
  const [resubmitForm, setResubmitForm] = useState({
    clarification_response: '',
    amount_requested: '',
    payment_due_date: '',
    purpose: ''
  });
  const [resubmitting, setResubmitting] = useState(false);
  
  // Status counts
  const [statusCounts, setStatusCounts] = useState({});

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/payment-requests/all`, {
        headers: getAuthHeader()
      });
      setRequests(response.data.requests || []);
      setStatusCounts(response.data.status_counts || {});
    } catch (error) {
      console.error('Error fetching requests:', error);
      toast.error('Failed to load payment requests');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/suppliers?is_active=true`, {
        headers: getAuthHeader()
      });
      setSuppliers(response.data || []);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  };

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: getAuthHeader()
      });
      setCurrentUser(response.data);
    } catch (error) {
      console.error('Error fetching current user:', error);
    }
  };

  useEffect(() => {
    fetchRequests();
    fetchSuppliers();
    fetchCurrentUser();
  }, [fetchRequests]);

  // Fetch supplier outstanding when supplier selected
  const handleSupplierChange = async (supplierId) => {
    const supplier = suppliers.find(s => s.id === supplierId);
    setCreateForm(prev => ({
      ...prev,
      supplier_id: supplierId,
      supplier_name: supplier?.name || ''
    }));
    
    if (supplierId) {
      try {
        const response = await axios.get(
          `${API_URL}/api/payment-requests/ledger/${supplierId}`,
          { headers: getAuthHeader() }
        );
        setSupplierOutstanding(response.data.summary);
      } catch (error) {
        console.error('Error fetching supplier outstanding:', error);
        setSupplierOutstanding(null);
      }
    } else {
      setSupplierOutstanding(null);
    }
  };

  const handleCreateRequest = async () => {
    if (!createForm.supplier_id || !createForm.amount_requested || !createForm.payment_due_date || !createForm.purpose) {
      toast.error('Please fill all required fields');
      return;
    }
    
    setCreating(true);
    try {
      const payload = {
        ...createForm,
        amount_requested: parseFloat(createForm.amount_requested)
      };
      
      await axios.post(`${API_URL}/api/payment-requests`, payload, {
        headers: getAuthHeader()
      });
      
      toast.success('Payment request submitted successfully');
      setCreateDialogOpen(false);
      setCreateForm({
        supplier_id: '',
        supplier_name: '',
        payment_type: 'against_invoice',
        amount_requested: '',
        payment_due_date: '',
        purpose: '',
        priority: 'normal',
        linked_grns: [],
        linked_pos: [],
        remarks: ''
      });
      setSupplierOutstanding(null);
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create request');
    } finally {
      setCreating(false);
    }
  };

  const openViewDialog = async (request) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/payment-requests/${request.id}`,
        { headers: getAuthHeader() }
      );
      setSelectedRequest(response.data);
      setViewDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load request details');
    }
  };

  const openResubmitDialog = (request) => {
    setSelectedRequest(request);
    setResubmitForm({
      clarification_response: '',
      amount_requested: request.amount_requested,
      payment_due_date: request.payment_due_date,
      purpose: request.purpose
    });
    setResubmitDialogOpen(true);
  };

  const handleResubmit = async () => {
    if (!selectedRequest) return;
    
    setResubmitting(true);
    try {
      await axios.patch(
        `${API_URL}/api/payment-requests/${selectedRequest.id}/resubmit`,
        resubmitForm,
        { headers: getAuthHeader() }
      );
      toast.success('Request resubmitted successfully');
      setResubmitDialogOpen(false);
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resubmit');
    } finally {
      setResubmitting(false);
    }
  };

  // Filter requests
  const filteredRequests = requests.filter(req => {
    const matchesStatus = filterStatus === 'all' || req.status === filterStatus;
    const matchesSupplier = filterSupplier === 'all' || req.supplier_id === filterSupplier;
    const matchesSearch = !searchTerm || 
      req.request_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      req.supplier_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      req.purpose?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSupplier && matchesSearch;
  });

  const canResubmit = (request) => {
    return currentUser?.id === request.created_by_id && 
           ['rejected', 'clarification_requested'].includes(request.status);
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="payment-requests-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Payment Requests</h1>
            <p className="text-muted-foreground">Manage payment requests to suppliers</p>
          </div>
          <Button onClick={() => setCreateDialogOpen(true)} data-testid="create-request-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Request
          </Button>
        </div>

        {/* Status Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card className="cursor-pointer hover:bg-muted/50" onClick={() => setFilterStatus('submitted')}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-blue-500" />
                <span className="text-sm text-muted-foreground">Pending</span>
              </div>
              <p className="text-2xl font-bold mt-1">{(statusCounts.submitted || 0) + (statusCounts.resubmitted || 0)}</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:bg-muted/50" onClick={() => setFilterStatus('approved')}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500" />
                <span className="text-sm text-muted-foreground">Approved</span>
              </div>
              <p className="text-2xl font-bold mt-1">{statusCounts.approved || 0}</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:bg-muted/50" onClick={() => setFilterStatus('due')}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-amber-500" />
                <span className="text-sm text-muted-foreground">Due</span>
              </div>
              <p className="text-2xl font-bold mt-1">{statusCounts.due || 0}</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:bg-muted/50" onClick={() => setFilterStatus('overdue')}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-500" />
                <span className="text-sm text-muted-foreground">Overdue</span>
              </div>
              <p className="text-2xl font-bold mt-1 text-red-600">{statusCounts.overdue || 0}</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:bg-muted/50" onClick={() => setFilterStatus('paid')}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <IndianRupee className="w-4 h-4 text-green-600" />
                <span className="text-sm text-muted-foreground">Paid</span>
              </div>
              <p className="text-2xl font-bold mt-1">{statusCounts.paid || 0}</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:bg-muted/50" onClick={() => setFilterStatus('all')}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-muted-foreground">All</span>
              </div>
              <p className="text-2xl font-bold mt-1">{requests.length}</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="relative flex-1 min-w-[200px] max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search requests..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="submitted">Submitted</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="due">Due</SelectItem>
                  <SelectItem value="overdue">Overdue</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filterSupplier} onValueChange={setFilterSupplier}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="All Suppliers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Suppliers</SelectItem>
                  {suppliers.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button variant="outline" size="icon" onClick={fetchRequests}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Requests Table */}
        <Card>
          <CardHeader>
            <CardTitle>Payment Requests</CardTitle>
            <CardDescription>{filteredRequests.length} request(s) found</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Loading...</div>
            ) : filteredRequests.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No payment requests found
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Request No.</TableHead>
                    <TableHead>Supplier</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created By</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRequests.map(req => (
                    <TableRow key={req.id} data-testid={`request-row-${req.id}`}>
                      <TableCell className="font-mono text-sm">{req.request_number}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-muted-foreground" />
                          {req.supplier_name}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">
                          {req.payment_type?.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {formatCurrency(req.approved_amount || req.amount_requested)}
                      </TableCell>
                      <TableCell>{formatDate(req.approved_payment_date || req.payment_due_date)}</TableCell>
                      <TableCell>{getPriorityBadge(req.priority)}</TableCell>
                      <TableCell>{getStatusBadge(req.status)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{req.created_by_name}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="ghost" onClick={() => openViewDialog(req)}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          {canResubmit(req) && (
                            <Button size="sm" variant="ghost" onClick={() => openResubmitDialog(req)}>
                              <Send className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Create Request Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <IndianRupee className="w-5 h-5" />
                New Payment Request
              </DialogTitle>
              <DialogDescription>
                Submit a request for payment to a supplier
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Supplier Selection */}
              <div className="space-y-2">
                <Label>Supplier *</Label>
                <SearchableSelect
                  options={suppliers.map(s => ({ value: s.id, label: s.name })).sort((a, b) => a.label.localeCompare(b.label))}
                  value={createForm.supplier_id}
                  onValueChange={handleSupplierChange}
                  placeholder="Select supplier..."
                  searchPlaceholder="Search supplier..."
                  emptyText="No suppliers found"
                />
              </div>

              {/* Supplier Outstanding Summary */}
              {supplierOutstanding && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-2 text-blue-700 font-medium mb-2">
                    <AlertTriangle className="w-4 h-4" />
                    Supplier Outstanding Summary
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div>
                      <span className="text-muted-foreground">Total Invoices:</span>
                      <span className="ml-2 font-semibold">{formatCurrency(supplierOutstanding.total_invoices)}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Total Paid:</span>
                      <span className="ml-2 font-semibold">{formatCurrency(supplierOutstanding.total_payments)}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Outstanding:</span>
                      <span className="ml-2 font-semibold text-red-600">{formatCurrency(supplierOutstanding.closing_balance)}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Payment Type */}
              <div className="space-y-2">
                <Label>Payment Type *</Label>
                <Select 
                  value={createForm.payment_type} 
                  onValueChange={(v) => setCreateForm(prev => ({ ...prev, payment_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="advance">Advance</SelectItem>
                    <SelectItem value="against_invoice">Against Invoice</SelectItem>
                    <SelectItem value="security_deposit">Security Deposit</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Amount and Due Date */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Amount Requested (₹) *</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="Enter amount"
                    value={createForm.amount_requested}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, amount_requested: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Payment Due Date *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !createForm.payment_due_date && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {createForm.payment_due_date ? format(new Date(createForm.payment_due_date), 'dd/MM/yyyy') : 'Select date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={createForm.payment_due_date ? new Date(createForm.payment_due_date) : undefined}
                        onSelect={(date) => setCreateForm(prev => ({ ...prev, payment_due_date: date ? format(date, 'yyyy-MM-dd') : '' }))}
                        disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {/* Priority */}
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select 
                  value={createForm.priority} 
                  onValueChange={(v) => setCreateForm(prev => ({ ...prev, priority: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Purpose */}
              <div className="space-y-2">
                <Label>Purpose / Reason *</Label>
                <Textarea
                  placeholder="Explain why this payment is needed..."
                  value={createForm.purpose}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, purpose: e.target.value }))}
                  rows={3}
                />
              </div>

              {/* Remarks */}
              <div className="space-y-2">
                <Label>Additional Remarks</Label>
                <Textarea
                  placeholder="Any additional notes..."
                  value={createForm.remarks}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, remarks: e.target.value }))}
                  rows={2}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleCreateRequest} disabled={creating}>
                {creating ? 'Submitting...' : 'Submit Request'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View Request Dialog */}
        <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                {selectedRequest?.request_number}
              </DialogTitle>
              <DialogDescription>
                Payment Request Details
              </DialogDescription>
            </DialogHeader>

            {selectedRequest && (
              <div className="space-y-4 py-4">
                {/* Status and Priority */}
                <div className="flex items-center gap-4">
                  {getStatusBadge(selectedRequest.status)}
                  {getPriorityBadge(selectedRequest.priority)}
                </div>

                {/* Clarification Question (if any) */}
                {selectedRequest.status === 'clarification_requested' && selectedRequest.clarification_question && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-center gap-2 text-amber-700 font-medium">
                      <AlertTriangle className="w-4 h-4" />
                      Clarification Needed
                    </div>
                    <p className="mt-1 text-sm text-amber-800">{selectedRequest.clarification_question}</p>
                  </div>
                )}

                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-muted-foreground">Supplier</Label>
                    <p className="font-medium">{selectedRequest.supplier_name}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Payment Type</Label>
                    <p className="font-medium capitalize">{selectedRequest.payment_type?.replace('_', ' ')}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Requested Amount</Label>
                    <p className="font-medium">{formatCurrency(selectedRequest.amount_requested)}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Requested Due Date</Label>
                    <p className="font-medium">{formatDate(selectedRequest.payment_due_date)}</p>
                  </div>
                  {selectedRequest.approved_amount && (
                    <>
                      <div>
                        <Label className="text-muted-foreground">Approved Amount</Label>
                        <p className="font-medium text-green-600">{formatCurrency(selectedRequest.approved_amount)}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Approved Due Date</Label>
                        <p className="font-medium">{formatDate(selectedRequest.approved_payment_date)}</p>
                      </div>
                    </>
                  )}
                </div>

                {/* Purpose */}
                <div>
                  <Label className="text-muted-foreground">Purpose</Label>
                  <p className="mt-1">{selectedRequest.purpose}</p>
                </div>

                {/* Modification Reason */}
                {selectedRequest.modification_reason && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <Label className="text-blue-700">Admin Modification Note</Label>
                    <p className="mt-1 text-sm">{selectedRequest.modification_reason}</p>
                  </div>
                )}

                {/* Payment Details */}
                {selectedRequest.payment && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2 text-green-700 font-medium mb-2">
                      <CheckCircle2 className="w-4 h-4" />
                      Payment Completed
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div><span className="text-muted-foreground">Amount:</span> {formatCurrency(selectedRequest.payment.amount_paid)}</div>
                      <div><span className="text-muted-foreground">Date:</span> {formatDate(selectedRequest.payment.payment_date)}</div>
                      <div><span className="text-muted-foreground">Mode:</span> {selectedRequest.payment.payment_mode?.toUpperCase()}</div>
                      <div><span className="text-muted-foreground">Reference:</span> {selectedRequest.payment.reference_no || selectedRequest.payment.cheque_no || '-'}</div>
                    </div>
                  </div>
                )}

                {/* History */}
                <div>
                  <Label className="text-muted-foreground">History</Label>
                  <div className="mt-2 space-y-2">
                    {selectedRequest.history?.map((h, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <ChevronRight className="w-3 h-3 text-muted-foreground" />
                        <span className="capitalize">{h.action.replace('_', ' ')}</span>
                        <span className="text-muted-foreground">by {h.by}</span>
                        <span className="text-muted-foreground">on {formatDate(h.at)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Created Info */}
                <div className="text-sm text-muted-foreground">
                  Created by {selectedRequest.created_by_name} on {formatDate(selectedRequest.created_at)}
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setViewDialogOpen(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Resubmit Dialog */}
        <Dialog open={resubmitDialogOpen} onOpenChange={setResubmitDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Resubmit Request</DialogTitle>
              <DialogDescription>
                Update and resubmit your payment request
              </DialogDescription>
            </DialogHeader>

            {selectedRequest?.clarification_question && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <Label className="text-amber-700">Question from Admin</Label>
                <p className="mt-1 text-sm">{selectedRequest.clarification_question}</p>
              </div>
            )}

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Response / Clarification</Label>
                <Textarea
                  placeholder="Provide clarification or explanation..."
                  value={resubmitForm.clarification_response}
                  onChange={(e) => setResubmitForm(prev => ({ ...prev, clarification_response: e.target.value }))}
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Amount (₹)</Label>
                  <Input
                    type="number"
                    value={resubmitForm.amount_requested}
                    onChange={(e) => setResubmitForm(prev => ({ ...prev, amount_requested: parseFloat(e.target.value) }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Due Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !resubmitForm.payment_due_date && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {resubmitForm.payment_due_date ? format(new Date(resubmitForm.payment_due_date), 'dd/MM/yyyy') : 'Select date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={resubmitForm.payment_due_date ? new Date(resubmitForm.payment_due_date) : undefined}
                        onSelect={(date) => setResubmitForm(prev => ({ ...prev, payment_due_date: date ? format(date, 'yyyy-MM-dd') : '' }))}
                        disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Updated Purpose</Label>
                <Textarea
                  value={resubmitForm.purpose}
                  onChange={(e) => setResubmitForm(prev => ({ ...prev, purpose: e.target.value }))}
                  rows={2}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setResubmitDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleResubmit} disabled={resubmitting}>
                {resubmitting ? 'Resubmitting...' : 'Resubmit'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
